import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import { updateProjectFunction } from '../features/projects/projectService';
import Swal from 'sweetalert2';

const EditProjectLayer = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Project State
    const [projectName, setProjectName] = useState("");
    const [customerId, setCustomerId] = useState("");
    const [customerName, setCustomerName] = useState("");
    const [location, setLocation] = useState("");
    const [projectType, setProjectType] = useState("");
    const [cost, setCost] = useState("");
    const [status, setStatus] = useState("");

    // Project Types State
    const [customProjectTypes, setCustomProjectTypes] = useState([
        "Residential",
        "Commercial",
        "Industrial"
    ]);

    // Project Type Search
    const [projectTypeSearch, setProjectTypeSearch] = useState("");
    const [isProjectTypeOpen, setIsProjectTypeOpen] = useState(false);

    // Search/Dropdown States
    const [searchTerm, setSearchTerm] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const [staffSearchTerm, setStaffSearchTerm] = useState("");
    const [isStaffDropdownOpen, setIsStaffDropdownOpen] = useState(false);

    const [selectedStaffId, setSelectedStaffId] = useState("");
    const [selectedStaffName, setSelectedStaffName] = useState("");

    const [errors, setErrors] = useState({});

    // Selectors
    const projectList = useSelector((state) => state.projects.projects);
    const customersList = useSelector((state) => state.customers.customers) || [];
    const staffList = useSelector((state) => state.staffs.staffs) || [];

    const project = useMemo(
        () => projectList.find((p) => p.id === id),
        [projectList, id]
    );

    // Populate fields when project is found
    useEffect(() => {
        if (project) {
            setProjectName(project.projectName || "");
            setCustomerId(project.customerId || "");
            setCustomerName(project.customerName || "");
            setSearchTerm(project.customerName || "");
            setLocation(project.location || "");

            setProjectType(project.projectType || "");
            setProjectTypeSearch(project.projectType || "");

            setCost(project.cost || "");
            setStatus(project.status || "Initialized");

            setSelectedStaffId(project.assignedStaffId || "");
            setSelectedStaffName(project.assignedStaffName || "");
            setStaffSearchTerm(project.assignedStaffName || "");

            if (
                project.projectType &&
                !customProjectTypes.includes(project.projectType)
            ) {
                setCustomProjectTypes((prev) => [
                    ...prev,
                    project.projectType
                ]);
            }
        }
    }, [project]);

    const validate = () => {
        const newErrors = {};

        if (!projectName.trim()) {
            newErrors.projectName = "Project Name is Required";
        }

        if (!customerId) {
            newErrors.customerName = "Please select a customer from the list";
        }

        if (!selectedStaffId) {
            newErrors.selectStaff = "Please select a staff / designer";
        }

        if (
            !projectType ||
            projectType === "Select Type" ||
            projectType === "Select the project type"
        ) {
            newErrors.projectType = "Please select the project type";
        }

        if (!location || !location.trim()) {
            newErrors.location = "Please enter the location";
        }

        if (!cost) {
            newErrors.fees = "Please enter the fees";
        } else if (isNaN(cost) || Number(cost) <= 0) {
            newErrors.fees =
                "Please enter a valid amount greater than 0";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const ErrorMsg = ({ field }) =>
        errors[field] ? (
            <div
                className="text-danger mt-1 fw-medium"
                style={{ fontSize: '11px' }}
            >
                {errors[field]}
            </div>
        ) : null;

    const filteredCustomers = useMemo(() => {
        return customersList.filter(
            (c) =>
                (c.name || "")
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                (c.location || "")
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
        );
    }, [customersList, searchTerm]);

    const filteredStaff = useMemo(() => {
        return staffList.filter(
            (s) =>
                (s.name || "")
                    .toLowerCase()
                    .includes(staffSearchTerm.toLowerCase()) ||
                (s.role || s.designation || "")
                    .toLowerCase()
                    .includes(staffSearchTerm.toLowerCase())
        );
    }, [staffList, staffSearchTerm]);

    const handleProjectUpdate = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        const payload = {
            id,
            projectName,
            location,
            customerName,
            customerId,
            assignedStaffId: selectedStaffId || null,
            assignedStaffName: selectedStaffName || null,
            cost,
            projectType,
            status
        };

        const success = await updateProjectFunction(
            dispatch,
            id,
            payload
        );

        if (success) {
            navigate(-1);
        }
    };

    const transparentStyle = {
        backgroundColor: "transparent",
        border: "none",
        boxShadow: "none",
        backdropFilter: "none"
    };

    return (
        <div className="card h-100 p-0 radius-12" style={transparentStyle}>
            <div className="card-body p-24">
                <div className="row justify-content-center">
                    <div className="col-xxl-6 col-xl-8 col-lg-10">
                        <div className="card border">
                            <div className="card-body">
                                <h6 className="text-lg text-center text-primary-light mb-16 py-3">
                                    Edit Project
                                </h6>

                                <form onSubmit={handleProjectUpdate}>

                                    {/* CUSTOMER SEARCH */}
                                    <div className="mb-20 position-relative">
                                        <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Select Customer <span className="text-danger-600">*</span>
                                        </label>

                                        <input
                                            type="text"
                                            className="form-control radius-8"
                                            value={searchTerm}
                                            onFocus={() => setIsDropdownOpen(true)}
                                            onBlur={() =>
                                                setTimeout(() => setIsDropdownOpen(false), 200)
                                            }
                                            onChange={(e) => {
                                                setSearchTerm(e.target.value);

                                                if (!e.target.value.trim()) {
                                                    setCustomerId("");
                                                    setCustomerName("");
                                                }
                                            }}
                                        />

                                        <ErrorMsg field="customerName" />

                                        {isDropdownOpen && (
                                            <ul
                                                className="position-absolute w-100 mt-1 bg-white radius-8 shadow-lg z-3 overflow-auto"
                                                style={{
                                                    maxHeight: '200px',
                                                    border: "1px solid #ddd",
                                                    listStyle: 'none',
                                                    padding: 0
                                                }}
                                            >
                                                {filteredCustomers.map((customer) => (
                                                    <li
                                                        key={customer.id}
                                                        className="p-10 border-bottom cursor-pointer hover-bg-primary-50"
                                                        onMouseDown={() => {
                                                            setCustomerId(customer.id);
                                                            setCustomerName(customer.name);
                                                            setSearchTerm(customer.name);
                                                        }}
                                                    >
                                                        <div className="fw-medium">
                                                            {customer.name}
                                                        </div>

                                                        <small>
                                                            {customer.address}
                                                        </small>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>

                                    {/* Project Name */}
                                    <div className="mb-20">
                                        <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Project Name <span className="text-danger-600">*</span>
                                        </label>

                                        <input
                                            type="text"
                                            className="form-control radius-8"
                                            value={projectName}
                                            onChange={(e) =>
                                                setProjectName(e.target.value)
                                            }
                                        />

                                        <ErrorMsg field="projectName" />
                                    </div>

                                    {/* STAFF SEARCH */}
                                    <div className="mb-20 position-relative">
                                        <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Assign Staff <span className="text-danger-600">*</span>
                                        </label>

                                        <input
                                            type="text"
                                            className="form-control radius-8"
                                            value={staffSearchTerm}
                                            onFocus={() =>
                                                setIsStaffDropdownOpen(true)
                                            }
                                            onBlur={() =>
                                                setTimeout(() => setIsStaffDropdownOpen(false), 200)
                                            }
                                            onChange={(e) => {
                                                setStaffSearchTerm(e.target.value);

                                                if (!e.target.value.trim()) {
                                                    setSelectedStaffId("");
                                                    setSelectedStaffName("");
                                                }
                                            }}
                                        />

                                        <ErrorMsg field="selectStaff" />

                                        {isStaffDropdownOpen && (
                                            <ul
                                                className="position-absolute w-100 mt-1 bg-white radius-8 shadow-lg z-3 overflow-auto"
                                                style={{
                                                    maxHeight: '200px',
                                                    border: "1px solid #ddd",
                                                    listStyle: 'none',
                                                    padding: 0
                                                }}
                                            >
                                                {filteredStaff.map((staff) => (
                                                    <li
                                                        key={staff.id || staff._id}
                                                        className="p-10 border-bottom cursor-pointer hover-bg-primary-50"
                                                        onMouseDown={() => {
                                                            setSelectedStaffId(
                                                                staff.id || staff._id
                                                            );

                                                            setSelectedStaffName(
                                                                staff.name
                                                            );

                                                            setStaffSearchTerm(
                                                                `${staff.name}`
                                                            );
                                                        }}
                                                    >
                                                        <div>
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <span className="fw-medium text-primary-light">
                                                                    {staff.name}
                                                                </span>

                                                                <span
                                                                    className="text-xs text-muted"
                                                                    style={{
                                                                        fontStyle: 'italic'
                                                                    }}
                                                                >
                                                                    {staff.role}
                                                                </span>
                                                            </div>

                                                            <small>
                                                                {staff?.location ||
                                                                    staff?.address}
                                                            </small>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>

                                    {/* Location */}
                                    <div className="mb-20">
                                        <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Location <span className="text-danger-600">*</span>
                                        </label>

                                        <input
                                            type="text"
                                            className="form-control radius-8"
                                            value={location}
                                            onChange={(e) =>
                                                setLocation(e.target.value)
                                            }
                                        />

                                        <ErrorMsg field="location" />
                                    </div>

                                    {/* Project type */}
                                    <div className="row mb-20">

                                        <div className="col-sm-6 position-relative">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                                Type <span className="text-danger-600">*</span>
                                            </label>

                                            <input
                                                type="text"
                                                className="form-control radius-8"
                                                placeholder="Search or type project type..."
                                                value={projectTypeSearch}
                                                onFocus={() =>
                                                    setIsProjectTypeOpen(true)
                                                }
                                                onBlur={() =>
                                                    setTimeout(() => setIsProjectTypeOpen(false), 200)
                                                }
                                                onChange={(e) => {
                                                    setProjectTypeSearch(
                                                        e.target.value
                                                    );

                                                    setProjectType(
                                                        e.target.value
                                                    );

                                                    setIsProjectTypeOpen(true);
                                                }}
                                            />

                                            <ErrorMsg field="projectType" />

                                            {isProjectTypeOpen && (
                                                <ul
                                                    className="position-absolute w-100 mt-1 bg-white radius-8 shadow-lg z-3 overflow-auto"
                                                    style={{
                                                        maxHeight: "200px",
                                                        listStyle: "none",
                                                        padding: 0,
                                                        border: "1px solid #e5e7eb"
                                                    }}
                                                >
                                                    {customProjectTypes
                                                        .filter((type) =>
                                                            type
                                                                .toLowerCase()
                                                                .includes(
                                                                    projectTypeSearch.toLowerCase()
                                                                )
                                                        )
                                                        .length > 0 ? (

                                                        customProjectTypes
                                                            .filter((type) =>
                                                                type
                                                                    .toLowerCase()
                                                                    .includes(
                                                                        projectTypeSearch.toLowerCase()
                                                                    )
                                                            )
                                                            .map((type) => (
                                                                <li
                                                                    key={type}
                                                                    className="p-10 border-bottom cursor-pointer hover-bg-primary-50"
                                                                    onMouseDown={(e) => {
                                                                        e.preventDefault();

                                                                        setProjectType(type);

                                                                        setProjectTypeSearch(
                                                                            type
                                                                        );

                                                                        setIsProjectTypeOpen(
                                                                            false
                                                                        );
                                                                    }}
                                                                >
                                                                    {type}
                                                                </li>
                                                            ))

                                                    ) : (
                                                        <li
                                                            className="p-10 text-primary-600 cursor-pointer fw-medium"
                                                            onMouseDown={(e) => {
                                                                e.preventDefault();

                                                                const trimmedValue =
                                                                    projectTypeSearch.trim();

                                                                if (!trimmedValue)
                                                                    return;

                                                                setCustomProjectTypes(
                                                                    (prev) => [
                                                                        ...new Set([
                                                                            ...prev,
                                                                            trimmedValue
                                                                        ])
                                                                    ]
                                                                );

                                                                setProjectType(
                                                                    trimmedValue
                                                                );

                                                                setProjectTypeSearch(
                                                                    trimmedValue
                                                                );

                                                                setIsProjectTypeOpen(
                                                                    false
                                                                );
                                                            }}
                                                        >
                                                            Use "
                                                            {projectTypeSearch}"
                                                        </li>
                                                    )}
                                                </ul>
                                            )}
                                        </div>

                                        <div className="col-sm-6">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                                Status <span className="text-danger-600">*</span>
                                            </label>

                                            <select
                                                className="form-control radius-8 form-select"
                                                value={status}
                                                onChange={(e) =>
                                                    setStatus(e.target.value)
                                                }
                                            >
                                                <option value="Initialized">
                                                    Initialized
                                                </option>

                                                <option value="In Progress">
                                                    In Progress
                                                </option>

                                                <option value="Completed">
                                                    Completed
                                                </option>

                                                <option value="On Hold">
                                                    On Hold
                                                </option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Fees */}
                                    <div className="mb-20">
                                        <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Fees <span className="text-danger-600">*</span>
                                        </label>

                                        <input
                                            type="text"
                                            className="form-control radius-8"
                                            value={cost}
                                            onChange={(e) =>
                                                setCost(e.target.value)
                                            }
                                        />

                                        <ErrorMsg field="fees" />
                                    </div>

                                    <div className="d-flex align-items-center justify-content-center gap-3">
                                        <button
                                            type="submit"
                                            className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                                        >
                                            Save Details
                                        </button>

                                        <button
                                            type='button'
                                            onClick={() => navigate(-1)}
                                            className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                                        >
                                            Cancel
                                        </button>
                                    </div>

                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditProjectLayer;